import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Collapse, Typography } from 'antd'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, TimeScale } from 'chart.js'
import { useCubeQuery } from '../../../utils/hooks/useCubeQuery'
import { useDebounce } from 'use-debounce'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import useCubeClient from '../../../utils/cubejs-client'
import { CompanyType } from '../../../__generated__/graphql'
import { useCurrentUser } from '../../../utils/CurrentUserContext'
import { PerformanceChartControls } from './PerformanceChartControls'
import { PerformanceChartChart } from './PerformanceChartChart'
import { METRICS, MetricKey, TimeGranularity, PerformanceChartProps, SummaryData } from './types'

const { Text } = Typography
const { Panel } = Collapse

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
)

const STORAGE_KEY = 'performanceChartCollapsed'

export const PerformanceChart: React.FC<PerformanceChartProps> = ({ 
  selectedCampaignIds,
  selectedCampaignNames 
}) => {
  // Get Cube.js API client and current user
  const cubejsApi = useCubeClient()
  const { user } = useCurrentUser()

  // Filter metrics based on company type
  const getVisibleMetrics = () => {
    const companyType = user?.company?.type
    
    if (companyType === CompanyType.Publisher) {
      // Publishers see: impressions, clicks, revenue, ecpm (but not cost)
      return METRICS.filter(m => m.key !== 'cost')
    } else {
      // Agencies/Advertisers see: impressions, clicks, cost, ecpm (but not revenue)
      return METRICS.filter(m => m.key !== 'revenue')
    }
  }

  const visibleMetrics = getVisibleMetrics()
  
  // State
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? stored === 'true' : true // Default to collapsed
  })
  
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>(['impressions', 'clicks', 'reads', 'clickouts'])
  const [barMetric, setBarMetric] = useState<MetricKey>('impressions')
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>('day')
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ])

  // Update bar metric to be the first selected metric when metrics change
  useEffect(() => {
    if (selectedMetrics.length > 0 && !selectedMetrics.includes(barMetric)) {
      setBarMetric(selectedMetrics[0])
    }
  }, [selectedMetrics, barMetric])

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed))
  }, [collapsed])

  // Debounced values for performance
  const [debouncedCampaignIds] = useDebounce(selectedCampaignIds, 300)
  const [debouncedMetrics] = useDebounce(selectedMetrics, 300)
  const [debouncedDateRange] = useDebounce(dateRange, 300)
  const [debouncedGranularity] = useDebounce(timeGranularity, 300)

  // Build Cube query
  const cubeQuery = useMemo(() => {
    if (!debouncedCampaignIds.length || !debouncedMetrics.length || !cubejsApi) return null

    const measures = debouncedMetrics
      .map(key => METRICS.find(m => m.key === key)?.measure)
      .filter(Boolean) as string[]

    return {
      measures,
      timeDimensions: [{
        dimension: 'aggregateTdLevel.date',
        dateRange: [
          debouncedDateRange[0].format('YYYY-MM-DD'),
          debouncedDateRange[1].format('YYYY-MM-DD')
        ],
        granularity: debouncedGranularity
      }],
      filters: [{
        dimension: 'aggregateTdLevel.campaignId',
        operator: 'equals',
        values: debouncedCampaignIds
      }],
      order: {
        'aggregateTdLevel.date': 'asc'
      },
      limit: 10000
    }
  }, [debouncedCampaignIds, debouncedMetrics, debouncedDateRange, debouncedGranularity, cubejsApi])

  // Build summary query (totals only)
  const summaryQuery = useMemo(() => {
    if (!debouncedCampaignIds.length || !cubejsApi) return null

    // Get measures for selected metrics only
    const selectedMeasures = selectedMetrics
      .map(key => METRICS.find(m => m.key === key)?.measure)
      .filter(Boolean) as string[]

    return {
      measures: selectedMeasures,
      filters: [
        {
          dimension: 'aggregateTdLevel.campaignId',
          operator: 'equals',
          values: debouncedCampaignIds
        },
        {
          dimension: 'aggregateTdLevel.date',
          operator: 'inDateRange',
          values: [
            debouncedDateRange[0].format('YYYY-MM-DD'),
            debouncedDateRange[1].format('YYYY-MM-DD')
          ]
        }
      ]
    }
  }, [debouncedCampaignIds, debouncedDateRange, selectedMetrics, cubejsApi])

  // Execute query
  const { resultSet, isLoading, error } = useCubeQuery(cubeQuery || {}, {
    skip: !cubeQuery || !cubejsApi,
    cubejsApi: cubejsApi as any
  })

  // Execute summary query
  const { resultSet: summaryResult, isLoading: summaryLoading } = useCubeQuery(summaryQuery || {}, {
    skip: !summaryQuery || !cubejsApi,
    cubejsApi: cubejsApi as any
  })

  // Calculate summary data
  const summaryData = useMemo(() => {
    if (!summaryResult) return null
    
    const data = summaryResult.rawData()
    if (!data || data.length === 0) return null
    
    // Sum up all the data points for selected metrics only
    const totals = data.reduce((acc: any, row: any) => {
      selectedMetrics.forEach(metricKey => {
        const metric = METRICS.find(m => m.key === metricKey)
        if (metric) {
          const value = row[metric.measure] || 0
          acc[metricKey] = (acc[metricKey] || 0) + value
        }
      })
      return acc
    }, {})
    
    return totals
  }, [summaryResult, selectedMetrics])

  // Handlers
  const handleMetricChange = useCallback((checkedValues: MetricKey[]) => {
    setSelectedMetrics(checkedValues)
  }, [])

  const handleDateRangeChange = useCallback((values: [Dayjs | null, Dayjs | null] | null) => {
    if (values && values[0] && values[1]) {
      setDateRange([values[0], values[1]])
    }
  }, [])

  // Render header summary
  const renderHeaderSummary = () => {
    const campaignText = selectedCampaignIds.length === 0 
      ? 'No campaigns selected'
      : selectedCampaignIds.length === 1
      ? selectedCampaignNames[selectedCampaignIds[0]] || 'Campaign'
      : selectedCampaignIds.length > 5
      ? `${selectedCampaignIds.length} campaigns selected`
      : `${selectedCampaignIds.length} campaigns`

    const metricText = selectedMetrics.map(key => 
      METRICS.find(m => m.key === key)?.label
    ).join(', ') || 'No metrics'

    const granularityText = {
      day: 'Daily',
      week: 'Weekly', 
      month: 'Monthly'
    }[timeGranularity]

    return (
      <div className="flex items-center gap-4">
        <Text strong>{campaignText}</Text>
        <Text type="secondary">{metricText}</Text>
        <Text type="secondary">{granularityText}</Text>
      </div>
    )
  }

  return (
    <div className="mb-6">
      <Collapse
        activeKey={collapsed ? [] : ['1']}
        onChange={(keys) => setCollapsed(keys.length === 0)}
        expandIconPosition="end"
      >
        <Panel 
          header={
            <div className="py-2">
              <Text strong className="text-lg">Performance Overview</Text>
              {collapsed && (
                <div className="mt-1">
                  {renderHeaderSummary()}
                </div>
              )}
            </div>
          }
          key="1"
        >
          <PerformanceChartControls
            selectedCampaignIds={selectedCampaignIds}
            selectedCampaignNames={selectedCampaignNames}
            selectedMetrics={selectedMetrics}
            onMetricChange={handleMetricChange}
            visibleMetrics={visibleMetrics}
            timeGranularity={timeGranularity}
            onTimeGranularityChange={setTimeGranularity}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            barMetric={barMetric}
            onBarMetricChange={setBarMetric}
            summaryData={summaryData}
            summaryLoading={summaryLoading}
          />

          {/* Chart */}
          <PerformanceChartChart
            resultSet={resultSet}
            selectedMetrics={selectedMetrics}
            barMetric={barMetric}
            timeGranularity={timeGranularity}
            selectedCampaignIds={selectedCampaignIds}
            isLoading={isLoading}
            error={error}
          />
        </Panel>
      </Collapse>
    </div>
  )
}
